import { createContext, useCallback, useContext, useEffect, useRef, useState } from 'react';

/**
 * One polite live region, mounted at app root and never unmounted.
 *
 * A live region inserted into the DOM at the same moment as its text is
 * unreliable across VoiceOver, NVDA and TalkBack — the node has to pre-exist
 * the change. It matters most in remote play, where the app's state changes
 * because a frame arrived from a server, with no user action to imply it: a
 * blind player would otherwise sit in silence and only discover the phase
 * changed by re-exploring the page.
 */
const AnnounceContext = createContext<(message: string) => void>(() => {});

export function useAnnounce() {
  return useContext(AnnounceContext);
}

export function AnnounceProvider({ children }: { children: React.ReactNode }) {
  const [message, setMessage] = useState('');
  const last = useRef('');

  const announce = useCallback((text: string) => {
    if (!text) return;
    // Identical text back-to-back is usually a double-render, but the same
    // sentence can also legitimately recur a round later. A zero-width space
    // makes the node's text change without changing what is spoken.
    setMessage(text === last.current ? `${text}\u200B` : text);
    last.current = text;
  }, []);

  return (
    <AnnounceContext.Provider value={announce}>
      {children}
      <div className="visually-hidden" role="status" aria-live="polite" aria-atomic="true">
        {message}
      </div>
    </AnnounceContext.Provider>
  );
}

/**
 * Moves focus to the screen's heading whenever `key` changes. Without it, a
 * server-driven screen change leaves focus on an element that no longer exists
 * and it reverts to <body> — the documented focus-loss failure, and a WCAG 2.2
 * SC 2.4.3 problem.
 */
export function useFocusOnChange<T extends HTMLElement>(key: unknown): React.RefCallback<T> {
  const ref = useRef<T | null>(null);
  useEffect(() => {
    ref.current?.focus();
  }, [key]);
  return useCallback((node: T | null) => {
    ref.current = node;
  }, []);
}

/**
 * Animates a number to `to` over 900ms, cubic ease-out — the reveal sweep, and
 * the only orchestrated motion in the app. Under `prefers-reduced-motion` it
 * jumps to the final value, but still resolves `onDone` asynchronously so
 * callers can't diverge between the two paths.
 */
export function useSweep(to: number, from: number, onDone?: () => void) {
  const [value, setValue] = useState(from);
  const doneRef = useRef(onDone);
  doneRef.current = onDone;

  useEffect(() => {
    const reduced = window.matchMedia?.('(prefers-reduced-motion: reduce)').matches;
    if (reduced) {
      setValue(to);
      const id = window.setTimeout(() => doneRef.current?.(), 0);
      return () => window.clearTimeout(id);
    }
    let raf = 0;
    let start: number | null = null;
    const step = (ts: number) => {
      if (start === null) start = ts;
      const k = Math.min(1, (ts - start) / 900);
      const eased = 1 - Math.pow(1 - k, 3);
      setValue(from + (to - from) * eased);
      if (k < 1) raf = requestAnimationFrame(step);
      else doneRef.current?.();
    };
    raf = requestAnimationFrame(step);
    return () => cancelAnimationFrame(raf);
    // `from` is the sweep's starting point, captured once per reveal.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [to]);

  return value;
}

/** A transient message. Errors say what happened and what to do. */
export function Toast({ text, onDone }: { text: string | null; onDone: () => void }) {
  useEffect(() => {
    if (!text) return;
    const id = window.setTimeout(onDone, 4200);
    return () => window.clearTimeout(id);
  }, [text, onDone]);
  if (!text) return null;
  return <div className="toast">{text}</div>;
}
